import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBranchesThunk } from "../../store/thunks/branchThunks";
import { OfficeBuildingIcon } from "@heroicons/react/outline";

const BranchSelector = ({
  selectedBranch,
  onBranchChange,
  showLabel = true,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { branches, loadingList } = useSelector((state) => state.branch);

  // Fetch branches on component mount
  useEffect(() => {
    dispatch(getBranchesThunk({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Get accessible branches for the user
  const getAccessibleBranches = () => {
    if (!user?.branchAccess) return [];

    const { primaryBranch, secondaryBranches = [] } = user.branchAccess;
    const accessibleBranchCodes = [primaryBranch, ...secondaryBranches].filter(
      Boolean
    );

    // If user is root or has special permissions, show all branches
    if (user.role === "root" || user.role === "super_admin") {
      return branches;
    }

    // Otherwise, filter branches based on user's access
    return branches.filter((branch) =>
      accessibleBranchCodes.includes(branch.code)
    );
  };

  const accessibleBranches = getAccessibleBranches();
  const selectedBranchData = accessibleBranches.find(
    (b) => b.code === selectedBranch
  );

  // Compact mode for mobile
  if (compact) {
    return (
      <div className="w-full">
        <select
          value={selectedBranch}
          onChange={(e) => onBranchChange(e.target.value)}
          disabled={loadingList || accessibleBranches.length <= 1}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white 
            disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {loadingList ? (
            <option>Loading...</option>
          ) : accessibleBranches.length === 0 ? (
            <option>No branches</option>
          ) : (
            accessibleBranches.map((branch) => (
              <option key={branch.code} value={branch.code}>
                {branch.name} ({branch.code})
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  // Full mode for tablet/desktop
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      {showLabel && (
        <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700">
          <OfficeBuildingIcon className="h-4 sm:h-5 w-4 sm:w-5 mr-1 text-gray-500" />
          <span className="hidden sm:inline">Branch:</span>
        </label>
      )}

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <select
          value={selectedBranch}
          onChange={(e) => onBranchChange(e.target.value)}
          disabled={loadingList || accessibleBranches.length <= 1}
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base border border-gray-300 
            rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white 
            disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {loadingList ? (
            <option>Loading branches...</option>
          ) : accessibleBranches.length === 0 ? (
            <option>No branches available</option>
          ) : (
            accessibleBranches.map((branch) => (
              <option key={branch.code} value={branch.code}>
                {branch.name} ({branch.code})
                {branch.code === user?.branchAccess?.primaryBranch &&
                  " - Primary"}
              </option>
            ))
          )}
        </select>

        {selectedBranchData?.rateMode && (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold self-center
              ${
                selectedBranchData.rateMode === "LIVE"
                  ? "bg-green-100 text-green-800"
                  : selectedBranchData.rateMode === "INHERIT"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
          >
            {selectedBranchData.rateMode}
          </span>
        )}
      </div>
    </div>
  );
};

export default BranchSelector;
